import type { MetaDataSchema } from '@mimir/common/mdf/metadataSchema';
import { DEFAULT_MDF_ID } from '@mimir/common/mdf/mdfDefaults';
import type { SetDefaultDateFieldPayload } from '../model/searchState';
import type { DefaultDateRangeFieldChoice } from '../pages/search/util';

/**
 * For a given field and metadata form, tries to find a matching date range filter choice.
 * Since fields can be referred to by their readable 'fieldId' or internal id (GUID value),
 * the function will search among all of the MDFs if needed to find a match.
 */
export function findDateFilterChoiceForField(
  choices: DefaultDateRangeFieldChoice[],
  fieldInfo: SetDefaultDateFieldPayload,
  mdfs: MetaDataSchema[],
) {
  const { fieldIdOrGuid, formId } = fieldInfo;
  const defaultMdf = mdfs.find(mdf => mdf.id === DEFAULT_MDF_ID);
  let candidate: DefaultDateRangeFieldChoice | undefined = undefined;
  for (const choice of choices) {
    if (choice.value === fieldIdOrGuid) {
      candidate = choice;
      break;
    }

    const choiceMdf = choice.customMdf ?? defaultMdf;
    if (!choiceMdf) continue;

    // If a formId is available, only consider the choice if it's from the same metadata form
    if (formId && (choiceMdf.id !== formId && choiceMdf.label !== formId)) continue;

    // If formId is not available, then only look for a match if a candidate wasn't already found
    if (!formId && candidate) continue;

    const matchingField = choiceMdf.fields.find(
      f => (f.id === fieldIdOrGuid || f.fieldId === fieldIdOrGuid)
        && (f.id === choice.value || f.fieldId === choice.value)
    );
    if (matchingField) candidate = choice;
  }

  return candidate;
}


